'use client';

import { useState, type FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { authClient } from '@/lib/auth-client';
import { Button } from '@/components/ui/Button';
import { TextField } from '@/components/ui/TextField';
import { FormMessage } from '@/components/ui/FormMessage';
import { Card } from '@/components/ui/Card';

// One shared login for everyone — buyers, vendors and admins alike.
// What a signed-in user can reach is decided server-side, per request,
// by src/lib/authz.ts (twelve rules #2, #3) — not by a separate
// "admin login" page.
export default function LoginPage() {
  const router = useRouter();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    const form = new FormData(event.currentTarget);
    const email = String(form.get('email') ?? '');
    const password = String(form.get('password') ?? '');

    const { error: signInError } = await authClient.signIn.email({ email, password });

    if (signInError) {
      // sendOnSignIn (auth.ts) has already mailed a fresh link by now.
      const unverified =
        signInError.code === 'EMAIL_NOT_VERIFIED' || /not verified/i.test(signInError.message ?? '');
      setError(
        unverified
          ? "Your email isn't verified yet. We've sent a new verification link — check your inbox, then sign in again."
          : (signInError.message ?? 'Could not sign in.')
      );
      setSubmitting(false);
      return;
    }

    router.push('/');
    router.refresh();
  }

  return (
    <main className="mx-auto flex max-w-md flex-1 flex-col justify-center px-4 py-16">
      <Card>
        <h1 className="mb-6 text-2xl font-semibold text-text-primary">Sign in</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
          <TextField label="Email" name="email" type="email" autoComplete="email" required />
          <TextField label="Password" name="password" type="password" autoComplete="current-password" required />
          {error && <FormMessage tone="error">{error}</FormMessage>}
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <div className="mt-6 flex flex-col gap-2 text-sm text-text-muted">
          <Link href="/reset-password" className="text-text-link underline">
            Forgot your password?
          </Link>
          <p>
            No account?{' '}
            <Link href="/register" className="text-text-link underline">
              Create one
            </Link>
          </p>
        </div>
      </Card>
    </main>
  );
}
